import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { MentorCommentsDisplay, MentorInlineComment } from './MentorComponents.jsx';
import { AnimalAvatar } from './AnimalAvatars.jsx';

import SayItYourWayCommunication from './SayItYourWayCommunication.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, FIREBASE_CONFIG, detectHarmContent, detectUnconstructiveContent, detectConnotationIssues, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';

function CommunicateStep({ conflict, user, canEdit, onUpdate, onReturnToOverview }) {
  const toast = useToast();
  const showCrisisModal = useCrisisAlert();
  const [becauseStatement, setBecauseStatement] = useState('Because ');
  const [paraphrase, setParaphrase] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [responseWindowRemaining, setResponseWindowRemaining] = useState(0);
  const [lateResponseReason, setLateResponseReason] = useState('');
  const [becauseStatementTimeRemaining, setBecauseStatementTimeRemaining] = useState(0);
  const [submitterWaitTimeRemaining, setSubmitterWaitTimeRemaining] = useState(0);
  const [validationShake, setValidationShake] = useState(false);
  const [becauseConnotation, setBecauseConnotation] = useState(null);
  const [paraphraseConnotation, setParaphraseConnotation] = useState(null);

  // AI Paraphrase Assistance State
  const [aiAssistAvailable, setAiAssistAvailable] = useState(false);
  const [aiAssistTrigger, setAiAssistTrigger] = useState(null); // 'deletions' | 'time' | 'validation'
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState(false);
  const [aiSuggestionsError, setAiSuggestionsError] = useState(null);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [selectedAiSuggestion, setSelectedAiSuggestion] = useState(null);
  const [aiAssistUsed, setAiAssistUsed] = useState(false);
  const [deletionCount, setDeletionCount] = useState(0);
  const [prevParaphraseLength, setPrevParaphraseLength] = useState(0);
  const [validationFailCount, setValidationFailCount] = useState(0);

  // AI Because Statement Assistance State (SIYW-powered)
  const [bsAiAssistAvailable, setBsAiAssistAvailable] = useState(false);
  const [bsAiAssistTrigger, setBsAiAssistTrigger] = useState(null);
  const [bsSiywOpen, setBsSiywOpen] = useState(false);
  const becauseFormRef = useRef(null);
  const [bsAiAssistUsed, setBsAiAssistUsed] = useState(false);
  const [bsDeletionCount, setBsDeletionCount] = useState(0);
  const [prevBecauseLength, setPrevBecauseLength] = useState(0);
  const [bsValidationFailCount, setBsValidationFailCount] = useState(0);

  // AI assist feature enabled for this conflict (default true for backwards compat)
  const aiAssistEnabled = conflict.aiParaphraseAssist !== false;

  // === PARAPHRASE QUALITY DETECTION ===
  // Detects when user is stating their own case instead of paraphrasing
  const [paraphraseWarning, setParaphraseWarning] = useState(null);
  
  const detectNonParaphrase = (text, originalStatement) => {
    if (!text || text.length < 25 || !originalStatement) return null;
    const origStr = typeof originalStatement === 'string' ? originalStatement : (originalStatement?.text || originalStatement?.description || JSON.stringify(originalStatement) || '');
    if (!origStr) return null;
    const lower = text.toLowerCase().trim();
    const originalLower = origStr.toLowerCase().trim();
    
    // Positive indicators that it IS a paraphrase — check these first
    // This list is intentionally broad to avoid false positives on genuine paraphrasing
    const paraphraseIndicators = [
      // Second-person reflection ("you feel...", "you're saying...")
      /\b(you (feel|felt|are feeling|said|mentioned|expressed|shared|believe|think|want|wanted|need|needed|prefer|described|explained|stated|indicated|brought up|pointed out|raised|highlighted|noted|conveyed|communicated|meant|were saying|are saying))\b/i,
      /\b(you'?re (saying|feeling|expressing|concerned|frustrated|worried|upset|afraid|hoping|trying to|describing|explaining|pointing out|asking for|looking for|wanting|needing))\b/i,
      // Third-person reflection using he/she/they pronouns
      /\b(they (feel|felt|said|mentioned|expressed|shared|believe|think|want|need|are saying|were saying))\b/i,
      /\b(their (concern|feeling|perspective|point|position|view|argument|frustration|side|experience|take))\b/i,
      /\b(he|she) (feels?|felt|said|mentioned|expressed|shared|believes?|thinks?|wants?|needs?|is saying|was saying|described|explained|stated)\b/i,
      /\b(his|her) (concern|feeling|perspective|point|position|view|frustration|side|experience|take|worry|preference|need|desire)\b/i,
      // Active listening / reflection frames
      /\b(what (i|you)'?re? (hearing|saying|understanding|getting|gathering|picking up))\b/i,
      /\b(it sounds like|sounds like you|what i hear|i hear you saying|i hear that|what i'?m hearing)\b/i,
      /\b(it seems like|seems like you|it appears that|from what you'?re saying)\b/i,
      /\b(if i understand|if i'?m understanding|let me (make sure|see if) i understand)\b/i,
      /\b(so (what )?you'?re (saying|telling|feeling|expressing)|so you (feel|think|believe|want|need|said))\b/i,
      /\b(so basically|so essentially|so in other words|in other words)\b/i,
      // Possessive reflection ("your concern...", "your perspective...")
      /\b(your (concern|feeling|perspective|point|frustration|worry|position|view|desire|wish|hope|issue|argument|side|experience|take|stance|reasoning|thought|preference|need|request))\b/i,
      // Perspective-taking frames
      /\b(from (your|their|his|her) (perspective|point of view|standpoint|end|side|angle))\b/i,
      /\b(you (are|were) (concerned|worried|frustrated|upset|hoping|saying|feeling|experiencing|dealing with|going through))\b/i,
      /\b(you'?d like|you would like|you prefer|you value|you wish|you hoped|you expected)\b/i,
      /\b(what matters to you|important to you|you care about|matters most to you)\b/i,
      /\b(the other (person|party|participant|side) (feels?|wants?|said|believes?|thinks?|needs?|expressed|is))\b/i,
      /\b(this person (feels?|wants?|said|believes?|thinks?|needs?|is))\b/i,
      /\b(understands? that|understanding is|in (their|your|his|her) (words|view|eyes|opinion))\b/i,
      // "I" statements that are actually paraphrase frames (not self-expression)
      /\b(i understand (that|you|your|why|how)|i'?m understanding)\b/i,
      /\b(i hear (you|that|what)|i can (see|hear|understand|tell) (that|why|how|you))\b/i,
      /\b(i (get|see) (that|what|where|why|how) you)\b/i,
      /\b(i think (you'?re|what you'?re) (saying|feeling|expressing|getting at|trying to say))\b/i,
      /\b(i (gather|sense|notice|recognize) (that|you))\b/i,
      /\b(what i(('?m)| am) (hearing|understanding|getting|gathering|taking away) (is|from))\b/i,
      /\b(the way i understand (it|this|your))\b/i,
      /\b(correct me if i'?m wrong|if i got (this|that|it) right)\b/i,
      // Summarizing and restating
      /\b(to (summarize|sum up|restate|put it another way|recap))\b/i,
      /\b(the (main|key|core|central|big|bottom) (point|issue|concern|thing|takeaway) (is|seems|here|you))\b/i,
      /\b(what you'?re (really )?saying|what this (comes|boils) down to)\b/i,
      // Natural paraphrase frames without pronouns
      /\b(the concern (here |being expressed )?(is|seems to be))\b/i,
      /\b(the (frustration|worry|issue|feeling|point|argument) (is|here is|seems to be))\b/i,
      /\b(what i understand is|what i'?m (understanding|taking away) is)\b/i,
      /\b(the point being made)\b/i,
      /\b(the (underlying|root|real) (concern|issue|frustration|feeling) (is|seems))\b/i,
      /\b(this comes down to|this is (really )?about)\b/i,
      // "You actually..." as reflection, not accusation
      /\b(you actually (want|need|feel|prefer|believe|think|said|meant))\b/i,
    ];
    
    const hasParaphraseLanguage = paraphraseIndicators.some(r => r.test(lower));
    if (hasParaphraseLanguage) return null; // Looks like a real paraphrase — don't flag
    
    // Indicators that the user is stating their OWN case instead of paraphrasing
    // Only match when it's clearly first-person self-expression with no reflection framing
    const selfStatementIndicators = [
      /^(i feel|i think|i believe|i need|i want|i don'?t|i am |i'm |i have |i had |i was |i've )/i,
      /\bi (don'?t|do not) (care|want|need|like|agree|think)\b/i,
      /\bi (just|really|honestly) (don'?t|want|need|think|feel)\b/i,
      /^(my (feeling|opinion|perspective|view|point|problem|issue|concern) is)/i,
      /^(the (problem|issue|thing) is that i)\b/i,
      /^because i feel/i,
      /\b(i couldn'?t care less|i could care less|whatever|who cares)\b/i,
    ];
    
    // Indicators that suggest argumentation, accusation, or hostility rather than reflection
    // IMPORTANT: These must be SPECIFIC hostile patterns, not just word presence.
    // Words like "unfair", "selfish" etc. commonly appear in legitimate paraphrases
    // (e.g., "You feel the situation is unfair") so they must NOT trigger here.
    const argumentIndicators = [
      /\b(you (always|never) )/i,  // Removed should/shouldn't/need to/have to — these appear in paraphrases
      /\b(you'?re (wrong|being difficult|being unreasonable|not listening))\b/i,
      /\b(that'?s not (true|fair|right|correct))\b/i,
      /\b(you don'?t (understand|listen|care|get it|realize))\b/i,
      /\b(you just want|you only want|all you (want|care|do|think))\b/i,
      /\b(you'?re trying to (cause|make|start|create))\b/i,
      /\b(give me a break|get over it|grow up|deal with it|stop being)\b/i,
      /\b(this is (stupid|dumb|pointless|ridiculous|a waste))\b/i,
      /\b(doesn'?t matter|don'?t matter|no one cares|nobody cares)\b/i,
      /\b(shut up|leave me alone|back off|mind your)\b/i,
      /\b((wrong|problem|fault) with you)\b/i,
      /\b(i blame you|it'?s your fault|you caused)\b/i,
    ];
    
    // Check for self-statement patterns
    const hasSelfStatement = selfStatementIndicators.some(r => r.test(lower));
    const hasArgument = argumentIndicators.some(r => r.test(lower));
    
    // Semantic similarity check — but be much more forgiving
    // A good paraphrase SHOULD use different words, so low word overlap is expected.
    // Only flag as "off topic" if there's virtually NO topical connection AND no
    // paraphrase framing language was detected above.
    const cleanOriginal = originalLower.replace(/[.,!?;:'"()-]/g, '');
    const cleanText = lower.replace(/[.,!?;:'"()-]/g, '');
    // Use content words (4+ chars) from the original as topic anchors
    const stopWords = new Set(['that', 'this', 'with', 'from', 'they', 'them', 'their', 'have', 'been', 'were', 'would', 'could', 'should', 'about', 'which', 'there', 'these', 'those', 'being', 'when', 'what', 'your', 'will', 'also', 'just', 'more', 'some', 'very', 'than', 'into', 'only', 'each', 'much', 'does', 'like', 'make', 'over', 'such', 'take', 'because', 'feel']);
    const originalWords = new Set(cleanOriginal.split(/\s+/).filter(w => w.length > 3 && !stopWords.has(w)));
    const textWords = cleanText.split(/\s+/).filter(w => w.length > 3 && !stopWords.has(w));
    const sharedWords = textWords.filter(w => originalWords.has(w));
    // Also check for related/synonym matches (stem-level: first 5 chars matching)
    const originalStems = new Set([...originalWords].map(w => w.substring(0, 5)));
    const stemMatches = textWords.filter(w => originalStems.has(w.substring(0, 5)));
    const combinedMatches = new Set([...sharedWords, ...stemMatches]);
    const similarity = originalWords.size > 0 ? combinedMatches.size / originalWords.size : 0;
    // Only flag if VERY low overlap AND the text is substantial (not just short)
    const lowSimilarity = similarity < 0.08 && text.length > 60;
    
    // Arguments get flagged immediately — they're clearly not paraphrasing
    if (hasArgument) {
      return {
        type: 'argument',
        message: 'This sounds like you\'re responding to or arguing against the other person\'s statement. Paraphrasing means reflecting back what they expressed, not reacting to it.',
        tip: 'Focus on their words: "You expressed that..." or "You\'re saying that...". You\'ll get to share your perspective in your next "Because" statement.'
      };
    }
    
    // Self-statements only flagged if there's also low similarity (to avoid false positives
    // on things like "I understand that you feel..." which starts with "I" but IS a paraphrase)
    if (hasSelfStatement && (lowSimilarity || similarity < 0.15)) {
      return {
        type: 'self_statement',
        message: 'It looks like you\'re sharing your own feelings or perspective. This section is for paraphrasing: restating what the other person said in your own words to show you understood them.',
        tip: 'Try starting with: "You feel..." or "What I hear you saying is..." or "Your concern is..."'
      };
    }
    
    // Off-topic only flagged with very low similarity AND no self-statement (to avoid double-flagging)
    if (lowSimilarity && !hasSelfStatement) {
      return {
        type: 'off_topic',
        message: 'Your response doesn\'t seem to relate to what the other person said. A paraphrase should restate their key points using your own words.',
        tip: 'Re-read their statement below and try to capture their main feeling and concern in your own words.'
      };
    }
    
    return null;
  };

  const isMentee = conflict.mentees?.some(m => m.id === user.id || m.email?.toLowerCase() === user.email?.toLowerCase()) ||
                   conflict.createdBy === user.id;
  const cycles = conflict.steps.communicate?.cycles || [];
  const maxCycles = conflict.maxCommunicationCycles || 5;

  const validateBecauseStatement = (statement) => {
    if (statement.length < 50 || statement.length > 250) return false;
    if (!statement.toLowerCase().startsWith('because')) return false;
    if (!statement.toLowerCase().includes('i feel')) return false;
    
    // Check for profanity
    const profanityList = [
      'damn', 'hell', 'crap', 'shit', 'shitty', 'fuck', 'fucking', 'fucked', 'fucker', 'bitch', 'bitchy', 'ass', 'bastard',
      'piss', 'pissed', 'dick', 'cock', 'pussy', 'whore', 'slut', 'fag', 'nigger',
      'nigga', 'retard', 'retarded', 'cunt', 'asshole', 'motherfucker', 'motherfucking',
      'bullshit', 'horseshit', 'dumbass', 'jackass', 'dipshit',
      'twat', 'wank', 'wanker', 'bloody', 'bugger', 'arse', 'bollocks', 'prick'
    ];
    
    const lowerStatement = statement.toLowerCase();
    const containsProfanity = profanityList.some(word => {
      // Check for whole word matches (with word boundaries)
      const regex = new RegExp(`\\b${word}\\b`, 'i');
      return regex.test(lowerStatement);
    });
    
    if (containsProfanity) return false;
    
    return true;
  };

  const containsProfanity = (statement) => {
    const profanityList = [
      'damn', 'hell', 'crap', 'shit', 'shitty', 'fuck', 'fucking', 'fucked', 'fucker', 'bitch', 'bitchy', 'ass', 'bastard',
      'piss', 'pissed', 'dick', 'cock', 'pussy', 'whore', 'slut', 'fag', 'nigger',
      'nigga', 'retard', 'retarded', 'cunt', 'asshole', 'motherfucker', 'motherfucking',
      'bullshit', 'horseshit', 'dumbass', 'jackass', 'dipshit',
      'twat', 'wank', 'wanker', 'bloody', 'bugger', 'arse', 'bollocks', 'prick'
    ];
    
    const lowerStatement = statement.toLowerCase();
    return profanityList.some(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'i');
      return regex.test(lowerStatement);
    });
  };

  const validateParaphrase = (text) => {
    if (text.length < 50 || text.length > 250) return false;
    if (containsProfanity(text)) return false;
    return true;
  };

  // === AI PARAPHRASE ASSISTANCE ===
  
  // Track deletions in paraphrase textarea
  const handleParaphraseChange = (newText) => {
    const lengthDrop = prevParaphraseLength - newText.length;
    if (lengthDrop >= 15) {
      const newCount = deletionCount + 1;
      setDeletionCount(newCount);
      if (newCount >= 2 && aiAssistEnabled && !aiAssistAvailable) {
        setAiAssistAvailable(true);
        setAiAssistTrigger('deletions');
      }
    }
    setPrevParaphraseLength(newText.length);
    setParaphrase(newText);
    
    // Run paraphrase quality detection
    const lastCycleEntry = cycles[cycles.length - 1];
    if (lastCycleEntry && !lastCycleEntry.paraphrase) {
      const warning = detectNonParaphrase(newText, lastCycleEntry.statement);
      setParaphraseWarning(warning);
    } else {
      setParaphraseWarning(null);
    }
  };

  // Check if enough time has passed to trigger AI assist (40% of response window)
  const checkTimeBasedTrigger = useCallback(() => {
    if (!aiAssistEnabled || aiAssistAvailable) return;
    const lastCycleEntry = cycles[cycles.length - 1];
    if (!lastCycleEntry || lastCycleEntry.paraphrase || lastCycleEntry.userId === user.id) return;
    
    const statementTime = new Date(lastCycleEntry.timestamp);
    const now = new Date();
    const minutesPassed = (now - statementTime) / (1000 * 60);
    // 40% of total window (cooldown + response window)
    const totalWindow = conflict.responseWaitTime + conflict.responseWindow;
    if (minutesPassed >= totalWindow * 0.4) {
      setAiAssistAvailable(true);
      setAiAssistTrigger('time');
    }
  }, [aiAssistEnabled, aiAssistAvailable, cycles, user.id, conflict.responseWaitTime, conflict.responseWindow]);

  // Time-based trigger check every 10 seconds
  React.useEffect(() => {
    if (!aiAssistEnabled || aiAssistAvailable) return;
    const interval = setInterval(checkTimeBasedTrigger, 10000);
    checkTimeBasedTrigger(); // check immediately
    return () => clearInterval(interval);
  }, [checkTimeBasedTrigger, aiAssistEnabled, aiAssistAvailable]);

  // Reset AI assist state when cycles change (new paraphrase needed)
  React.useEffect(() => {
    setAiAssistAvailable(false);
    setAiAssistTrigger(null);
    setAiSuggestions([]);
    setAiSuggestionsLoading(false);
    setAiSuggestionsError(null);
    setAiPanelOpen(false);
    setSelectedAiSuggestion(null);
    setAiAssistUsed(false);
    setDeletionCount(0);
    setPrevParaphraseLength(0);
    setValidationFailCount(0);
  }, [cycles.length]);

  // Trigger AI assist on validation failure
  const handleValidationFailure = () => {
    if (!aiAssistEnabled) return;
    const newCount = validationFailCount + 1;
    setValidationFailCount(newCount);
    if (!aiAssistAvailable) {
      setAiAssistAvailable(true);
      setAiAssistTrigger('validation');
    }
  };

  // Shared helper for calling the Cloud Function with XMLHttpRequest fallback
  // Some privacy-focused browsers (DuckDuckGo, Brave) may block fetch to cross-origin URLs
  const callAiFunction = (payload) => {
    const projectNumber = FIREBASE_CONFIG.messagingSenderId;
    const functionUrl = `https://generateparaphrasesuggestions-${projectNumber}.us-central1.run.app`;
    // Always include userId for authentication
    const bodyStr = JSON.stringify({ ...payload, userId: user.id });

    // Try fetch first, fall back to XMLHttpRequest
    return new Promise(async (resolve, reject) => {
      const token = await window.firebaseAuth?.currentUser?.getIdToken(false).catch(() => null);
      // Attempt 1: fetch with cors mode
      try {
        const response = await fetch(functionUrl, {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: bodyStr
        });
        if (!response.ok) {
          let errorMsg = `Server error: ${response.status}`;
          try {
            const errorData = await response.json();
            if (errorData.error) errorMsg = errorData.error;
          } catch (e) {}
          throw new Error(errorMsg);
        }
        const data = await response.json();
        return resolve(data);
      } catch (fetchError) {
      }

      // Attempt 2: XMLHttpRequest (works in browsers that block fetch but allow XHR)
      try {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", functionUrl, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Accept", "application/json");
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.timeout = 30000;

        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid JSON response'));
            }
          } else {
            let errorMsg = `Server error: ${xhr.status}`;
            try {
              const errorData = JSON.parse(xhr.responseText);
              if (errorData.error) errorMsg = errorData.error;
            } catch (e) {}
            reject(new Error(errorMsg));
          }
        };
        xhr.onerror = function() { reject(new Error('Network request failed')); };
        xhr.ontimeout = function() { reject(new Error('Request timed out')); };
        xhr.send(bodyStr);
      } catch (xhrError) {
        reject(new Error(`Both fetch and XHR failed: ${xhrError.message}`));
      }
    });
  };

  // Generate AI paraphrase suggestions via Firebase Cloud Function
  const generateAiSuggestions = async () => {
    const lastCycleEntry = cycles[cycles.length - 1];
    if (!lastCycleEntry) return;
    
    setAiSuggestionsLoading(true);
    setAiSuggestionsError(null);
    setAiPanelOpen(true);
    
    try {
      const data = await callAiFunction({
        statement: lastCycleEntry.statement,
        conflictId: conflict.id
      });
      
      if (data.suggestions && Array.isArray(data.suggestions) && data.suggestions.length === 3) {
        setAiSuggestions(data.suggestions);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('AI suggestion generation failed:', error);
      const msg = error.message?.includes('429') || error.message?.includes('Rate limit')
        ? 'Rate limit reached. Please wait a bit before requesting more suggestions.'
        : error.message?.includes('401') || error.message?.includes('403')
        ? 'Authentication error. Please refresh the page and try again.'
        : 'Failed to generate suggestions. Please try again or write your own paraphrase.';
      setAiSuggestionsError(msg);
    } finally {
      setAiSuggestionsLoading(false);
    }
  };

  // Check if edited paraphrase meets the modification requirement
  const meetsEditRequirement = (text) => {
    if (!aiAssistUsed || aiSuggestions.length === 0) return true; // no AI used, no requirement
    
    // Must not be an exact substring of any suggestion
    for (const suggestion of aiSuggestions) {
      if (suggestion.includes(text) || text === suggestion) return false;
    }
    
    // Must differ by at least 10 characters from the selected suggestion
    if (selectedAiSuggestion !== null) {
      const original = aiSuggestions[selectedAiSuggestion];
      let diffCount = 0;
      const maxLen = Math.max(text.length, original.length);
      for (let i = 0; i < maxLen; i++) {
        if (text[i] !== original[i]) diffCount++;
      }
      if (diffCount < 10) return false;
    }
    
    return true;
  };

  // === AI BECAUSE STATEMENT ASSISTANCE ===

  // Track deletions in Because statement textarea
  const handleBecauseChange = (newText) => {
    // Protect the "Because " prefix — restore it if the user deletes it
    if (!newText.toLowerCase().startsWith('because ') && !newText.toLowerCase().startsWith('because')) {
      newText = 'Because ' + newText;
    } else if (newText.toLowerCase() === 'because' || newText.toLowerCase() === 'becaus' || newText.toLowerCase() === 'becau' || newText.toLowerCase() === 'beca' || newText.toLowerCase() === 'bec' || newText.toLowerCase() === 'be' || newText.toLowerCase() === 'b' || newText === '') {
      newText = 'Because ';
    }
    
    const lengthDrop = prevBecauseLength - newText.length;
    if (lengthDrop >= 15) {
      const newCount = bsDeletionCount + 1;
      setBsDeletionCount(newCount);
      if (newCount >= 2 && aiAssistEnabled && !bsAiAssistAvailable) {
        setBsAiAssistAvailable(true);
        setBsAiAssistTrigger('deletions');
      }
    }
    setPrevBecauseLength(newText.length);
    setBecauseStatement(newText);
    
    // Claim active composer on first keystroke for the first communication entry
    if (newText.length > 0 && cycles.length === 0 && activeComposer !== user.id) {
      claimActiveComposer();
    }
  };

  // Time-based trigger for Because statement (40% of response window after last communication entry)
  const checkBsTimeBasedTrigger = useCallback(() => {
    if (!aiAssistEnabled || bsAiAssistAvailable) return;
    
    let referenceTime = null;
    
    if (cycles.length === 0) {
      // First statement: measure from when communicate phase started
      const communicateEntry = conflict.statusHistory?.filter(e => e.status === 'communicate').pop();
      if (communicateEntry) {
        referenceTime = new Date(communicateEntry.timestamp);
      }
    } else {
      // Subsequent statements: measure from the last paraphrase (when this user's turn began)
      const lastCycleEntry = cycles[cycles.length - 1];
      if (lastCycleEntry?.paraphrasedAt) {
        referenceTime = new Date(lastCycleEntry.paraphrasedAt);
      }
    }
    
    if (!referenceTime) return;
    
    const now = new Date();
    const minutesPassed = (now - referenceTime) / (1000 * 60);
    if (minutesPassed >= conflict.responseWindow * 0.4) {
      setBsAiAssistAvailable(true);
      setBsAiAssistTrigger('time');
    }
  }, [aiAssistEnabled, bsAiAssistAvailable, cycles, conflict.responseWindow, conflict.statusHistory]);

  React.useEffect(() => {
    if (!aiAssistEnabled || bsAiAssistAvailable) return;
    const interval = setInterval(checkBsTimeBasedTrigger, 10000);
    checkBsTimeBasedTrigger();
    return () => clearInterval(interval);
  }, [checkBsTimeBasedTrigger, aiAssistEnabled, bsAiAssistAvailable]);

  // Reset Because AI state when cycles change
  React.useEffect(() => {
    setBsAiAssistAvailable(false);
    setBsAiAssistTrigger(null);
    setBsSiywOpen(false);
    setBsAiAssistUsed(false);
    setBsDeletionCount(0);
    setPrevBecauseLength(0);
    setBsValidationFailCount(0);
  }, [cycles.length]);

  // Trigger Because AI assist on validation failure
  const handleBsValidationFailure = () => {
    if (!aiAssistEnabled) return;
    setBsValidationFailCount(prev => prev + 1);
    if (!bsAiAssistAvailable) {
      setBsAiAssistAvailable(true);
      setBsAiAssistTrigger('validation');
    }
  };

  // Open SIYW panel for Because statement assistance
  const openBsSiyw = () => {
    setBsSiywOpen(true);
  };

  // Handle SIYW extraction — fills in the Because statement
  const handleBsSiywExtracted = (extractedBecause) => {
    if (extractedBecause) {
      // Ensure it starts with "Because "
      let statement = extractedBecause;
      if (!statement.toLowerCase().startsWith('because ')) {
        statement = 'Because ' + statement;
      }
      handleBecauseChange(statement);
      setBsAiAssistUsed(true);
      setBsSiywOriginal(statement);
      // Scroll the "Share Your Perspective" form to top of viewport
      // so user can see the drafted statement, edit it, and submit
      setTimeout(() => {
        becauseFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  };

  // Check if edited Because statement meets modification requirement (SIYW version)
  // The user must change at least 10 characters from what SIYW generated
  const [bsSiywOriginal, setBsSiywOriginal] = useState(null);
  
  const meetsBsEditRequirement = (text) => {
    if (!bsAiAssistUsed) return true;
    if (!bsSiywOriginal) return true;
    
    // Exact match = not edited
    if (text === bsSiywOriginal) return false;
    
    // Count character differences
    let diffCount = 0;
    const maxLen = Math.max(text.length, bsSiywOriginal.length);
    for (let i = 0; i < maxLen; i++) {
      if (text[i] !== bsSiywOriginal[i]) diffCount++;
    }
    return diffCount >= 10;
  };

  const handleSubmitBecause = async () => {
    if (!validateBecauseStatement(becauseStatement)) {
      handleBsValidationFailure();
      // Trigger shake animation on the checklist instead of a plain alert
      setValidationShake(true);
      setTimeout(() => setValidationShake(false), 820);
      return;
    }

    // Check AI edit requirement
    if (bsAiAssistUsed && !meetsBsEditRequirement(becauseStatement)) {
      toast('You must edit the AI suggestion more before submitting. Change at least 10 characters and make it your own.');
      return;
    }

    // Check if this is a late submission (only after first cycle)
    const isLate = cycles.length > 0 && isBecauseStatementLate();
    
    if (isLate && lateResponseReason.trim().length < 10) {
      toast('Please provide a reason for submitting late (minimum 10 characters)');
      return;
    }
    if (isLate && containsProfanity(lateResponseReason)) {
      toast('Your late submission reason contains inappropriate language. Please revise it.');
      return;
    }

    // Block unconstructive/nasty content
    const toneCheck = detectUnconstructiveContent(becauseStatement);
    if (toneCheck.isUnconstructive) {
      toast(toneCheck.reason || 'Please keep your statement respectful and constructive.');
      return;
    }

    // Check for inflammatory connotations — warn but allow override
    const connotation = detectConnotationIssues(becauseStatement);
    if (connotation.hasIssues && !becauseConnotation?._dismissed) {
      setBecauseConnotation(connotation);
      return;
    }

    const newCycle = {
      userId: user.id,
      userName: user.name,
      userAvatar: user.avatar,
      userEmail: user.email,
      statement: becauseStatement,
      paraphrase: null,
      timestamp: new Date().toISOString(),
      ...(bsAiAssistUsed && {
        statementAiAssisted: true,
        statementAiAssistTrigger: bsAiAssistTrigger
      }),
      ...(isLate && {
        lateSubmission: true,
        lateReason: lateResponseReason.trim(),
        minutesLate: Math.ceil(Math.abs(becauseStatementTimeRemaining))
      })
    };

    const updated = {
      ...conflict,
      steps: {
        ...conflict.steps,
        communicate: {
          ...conflict.steps.communicate,
          cycles: [...cycles, newCycle],
          // Clear active composer after submission
          activeComposer: null,
          activeComposerTimestamp: null,
          // Clear previous satisfaction votes — new exchange invalidates old votes
          satisfactionVotes: {},
          lastVoteResult: null,
          lastVoteTimestamp: null
        }
      },
      updatedAt: new Date().toISOString(),
      updatedBy: user.id
    };

    addAuditEntry(updated, user.id, user.name, 'Submitted Because statement', `Communication cycle ${Math.ceil((cycles.length + 1) / 2)}`);
    await storage.set(`conflict:${conflict.id}`, updated, true);
    // Harm detection — check after saving (never block the user)
    const harmResult = detectHarmContent(becauseStatement);
    if (harmResult.detected) {
      showCrisisModal(harmResult.type);
      notifyMentorOfHarmDetection(conflict, user, harmResult.type, 'Because statement');
    }
    setBecauseStatement('Because ');
    setLateResponseReason('');
    toast('Statement submitted! Waiting for the other participant to paraphrase.');
    await onUpdate();
    // User is now waiting for the other person to paraphrase — return to overview
    if (onReturnToOverview) await onReturnToOverview();
  };

  const handleSubmitParaphrase = async (cycleIdx) => {
    // Validate paraphrase
    if (!validateParaphrase(paraphrase)) {
      handleValidationFailure();
      toast('Paraphrase must be 50-250 characters with no profanity.');
      return;
    }

    // Check AI edit requirement if AI was used
    if (aiAssistUsed && !meetsEditRequirement(paraphrase)) {
      toast('You must edit the AI suggestion more before submitting. Change at least 10 characters and make it your own.');
      return;
    }

    // Warn if the paraphrase quality detection flagged an issue
    if (paraphraseWarning) {
      const warningMessages = {
        self_statement: 'It looks like you\'re sharing your own perspective instead of paraphrasing. A paraphrase should restate what the OTHER person said.\n\nAre you sure you want to submit this?',
        argument: 'This appears to be a counter-argument rather than a paraphrase. Paraphrasing means reflecting back what the other person expressed. You\'ll get to share your perspective in your next "Because" statement.\n\nAre you sure you want to submit this?',
        off_topic: 'Your response doesn\'t seem to closely relate to what the other person said.\n\nAre you sure you want to submit this?'
      };
      if (!window.confirm(warningMessages[paraphraseWarning.type] || 'Are you sure you want to submit this?')) {
        return;
      }
    }

    // Check if response is late (uses timer-driven state so display and logic agree)
    const cycle = cycles[cycleIdx];
    const statementTime = new Date(cycle.timestamp);
    const now = new Date();
    const minutesPassed = (now - statementTime) / (1000 * 60);
    const totalResponseWindow = conflict.responseWaitTime + conflict.responseWindow;
    const isLate = paraphraseIsLate;

    if (isLate && !lateResponseReason.trim()) {
      toast('Please provide a reason for the late response before submitting.');
      return;
    }
    if (isLate && containsProfanity(lateResponseReason)) {
      toast('Your late response reason contains inappropriate language. Please revise it.');
      return;
    }

    // Block unconstructive/nasty content
    const toneCheck = detectUnconstructiveContent(paraphrase);
    if (toneCheck.isUnconstructive) {
      toast(toneCheck.reason || 'Please keep your paraphrase respectful and constructive.');
      return;
    }

    // Check for inflammatory connotations — warn but allow override
    const pConnotation = detectConnotationIssues(paraphrase);
    if (pConnotation.hasIssues && !paraphraseConnotation?._dismissed) {
      setParaphraseConnotation(pConnotation);
      return;
    }

    const updatedCycles = [...cycles];
    updatedCycles[cycleIdx] = {
      ...updatedCycles[cycleIdx],
      paraphrase,
      paraphrasedBy: user.id,
      paraphrasedAt: new Date().toISOString(),
      ...(aiAssistUsed && {
        aiAssisted: true,
        aiAssistTrigger: aiAssistTrigger
      }),
      ...(isLate && {
        lateResponse: true,
        lateResponseReason: lateResponseReason.trim(),
        minutesLate: Math.ceil(minutesPassed - totalResponseWindow)
      })
    };

    const updated = {
      ...conflict,
      steps: {
        ...conflict.steps,
        communicate: {
          ...conflict.steps.communicate,
          cycles: updatedCycles
        }
      },
      updatedAt: new Date().toISOString(),
      updatedBy: user.id
    };

    addAuditEntry(updated, user.id, user.name, 'Submitted paraphrase', 'Communication phase');
    await storage.set(`conflict:${conflict.id}`, updated, true);
    // Harm detection — check after saving (never block the user)
    const harmResult = detectHarmContent(paraphrase);
    if (harmResult.detected) {
      showCrisisModal(harmResult.type);
      notifyMentorOfHarmDetection(conflict, user, harmResult.type, 'Communication paraphrase');
    }
    setParaphrase('');
    setLateResponseReason('');
    onUpdate();
  };

  // === ALTERNATING TURN-TAKING LOGIC ===
  // A complete communication cycle:
  //   1. Mentee A submits Because → 2. Mentee B waits cooldown then paraphrases →
  //   3. Mentee B immediately submits their Because → 4. Mentee A waits cooldown then paraphrases
  // That's 2 data entries (Because+paraphrase pairs) per cycle. 5 cycles = 10 entries max.
  
  const lastCycle = cycles.length > 0 ? cycles[cycles.length - 1] : null;
  const maxEntries = maxCycles * 2; // 5 full cycles × 2 entries each = 10
  
  // Basic eligibility: is a mentee, can edit, and under max entries
  const canAddStatement = isMentee && canEdit && cycles.length < maxEntries;
  
  // Does the last cycle need a paraphrase from someone other than the submitter?
  // Only mentees who can edit should see the paraphrase form (not observers/mentors)
  const needsParaphrase = isMentee && canEdit && lastCycle && !lastCycle.paraphrase && lastCycle.userId !== user.id;
  
  // Is the current user waiting for someone else to paraphrase their statement?
  const isWaitingForOtherMentee = lastCycle && lastCycle.userId === user.id && !lastCycle.paraphrase;
  
  // === FIRST ENTRY PRIORITY & ACTIVE COMPOSER LOCKING ===
  // For the first Because statement (cycles.length === 0):
  //   - The mentee who voted "Continue to Communication" (the rejector) has priority
  //   - If the response window expires with no entry, any mentee can go first
  //   - Once a mentee starts typing, their id is saved as activeComposer, locking out others
  const activeComposer = conflict.steps?.communicate?.activeComposer || null;
  const activeComposerTimestamp = conflict.steps?.communicate?.activeComposerTimestamp || null;
  
  // Check if response window has expired since communication phase started (for first entry only)
  const getFirstEntryResponseWindowExpired = () => {
    if (cycles.length > 0) return false;
    const communicateEntry = conflict.statusHistory?.filter(e => e.status === 'communicate').pop();
    if (!communicateEntry) return false;
    const communicateStartTime = new Date(communicateEntry.timestamp);
    const now = new Date();
    const minutesSinceStart = (now - communicateStartTime) / (1000 * 60);
    return minutesSinceStart > (conflict.responseWindow || 15);
  };
  const firstEntryWindowExpired = getFirstEntryResponseWindowExpired();
  
  // Check if active composer has timed out (5 minutes with no submission)
  const activeComposerTimedOut = (() => {
    if (!activeComposer || !activeComposerTimestamp) return false;
    const now = new Date();
    const composerTime = new Date(activeComposerTimestamp);
    const minutesPassed = (now - composerTime) / (1000 * 60);
    return minutesPassed > 5; // 5-minute timeout for composing
  })();
  
  // The rejector is the person who moved the conflict to communicate phase
  const rejectorId = (() => {
    // Find who voted "no" (continue to communication) in the skip communication vote
    const votes = conflict.steps?.identifyDefine?.skipCommunicationVotes || {};
    const noVoter = Object.values(votes).find(v => !v.skip);
    return noVoter?.userId || conflict.updatedBy || null;
  })();
  
  // Determine whose turn it is to submit the next Because statement
  const lastParaphraserId = lastCycle?.paraphrasedBy || null;
  const isMyTurnToSubmitBecause = (() => {
    // First entry (no cycles yet): enforce priority
    if (cycles.length === 0) {
      // If someone is actively composing (and hasn't timed out), only they can submit
      if (activeComposer && !activeComposerTimedOut) {
        return activeComposer === user.id;
      }
      // If response window expired, any mentee can go
      if (firstEntryWindowExpired) return true;
      // Otherwise, only the rejector has priority
      return rejectorId === user.id;
    }
    // Last entry not yet paraphrased: nobody can submit a new Because yet
    if (!lastCycle.paraphrase) return false;
    // Last entry paraphrased: only the paraphraser can submit next
    return lastParaphraserId === user.id;
  })();
  
  // Is this user blocked because another mentee is the active composer?
  const isBlockedByActiveComposer = cycles.length === 0 && activeComposer && activeComposer !== user.id && !activeComposerTimedOut;
  
  // Did the current user just paraphrase the last entry? (for UI hints)
  const didJustParaphrase = lastCycle?.paraphrase && lastParaphraserId === user.id;
  
  // Is the other mentee waiting because it's not their turn?
  const isWaitingForOtherTurn = lastCycle?.paraphrase && lastParaphraserId && lastParaphraserId !== user.id && cycles.length < maxEntries;
  
  // How many complete cycles (each = 2 entries) have been completed?
  const completedCycles = Math.floor(cycles.filter(c => c.paraphrase).length / 2);
  
  // Combined: can this user submit a Because statement right now?
  // (Further gated by pendingSatisfactionVote below after vote variables are declared)
  const canSubmitBecauseBase = canAddStatement && !needsParaphrase && !isWaitingForOtherMentee && isMyTurnToSubmitBecause;
  
  // Claim active composer status when user starts typing (first entry only)
  const claimActiveComposer = async () => {
    if (cycles.length !== 0 || activeComposer === user.id) return;
    const updated = {
      ...conflict,
      steps: {
        ...conflict.steps,
        communicate: {
          ...conflict.steps.communicate,
          activeComposer: user.id,
          activeComposerTimestamp: new Date().toISOString()
        }
      },
      updatedAt: new Date().toISOString()
    };
    await storage.set(`conflict:${conflict.id}`, updated, true);
    onUpdate();
  };

  // === COMMUNICATION SATISFACTION VOTE ===
  // Determines if the communication cycle is in a "complete" state where voting to move on is allowed
  // Conditions: at least 1 complete cycle (Because + paraphrase), last entry is paraphrased, no pending work
  // This should be visible to BOTH mentees regardless of whose turn it is to submit next
  const communicationIsComplete = (() => {
    if (cycles.length === 0) return false;
    const lastEntry = cycles[cycles.length - 1];
    // Last entry must be paraphrased (no pending paraphrase work)
    if (!lastEntry?.paraphrase) return false;
    // Every active mentee must have submitted at least one Because statement
    // that has been paraphrased before the "move on" vote appears.
    // Works for 2, 3, or any number of participants.
    const activeMenteeIds = conflict.mentees?.map(m => m.id) || [];
    const menteesWithCompletedEntry = new Set(cycles.filter(c => c.paraphrase).map(c => c.userId));
    return activeMenteeIds.length > 0 && activeMenteeIds.every(id => menteesWithCompletedEntry.has(id));
  })();

  const satisfactionVotes = conflict.steps?.communicate?.satisfactionVotes || {};
  const userSatisfactionVote = satisfactionVotes[user.id];
  const menteeIds = conflict.mentees?.map(m => m.id) || [];
  const allSatisfactionVoted = menteeIds.every(id => satisfactionVotes[id] !== undefined);
  const allSatisfied = allSatisfactionVoted && Object.values(satisfactionVotes).every(v => v.satisfied);
  const anySaidContinue = Object.values(satisfactionVotes).some(v => !v.satisfied);

  // Final gate: block Because statement and waiting blocks during any active satisfaction vote
  // Active vote = communication is complete AND either:
  //   (a) current user hasn't voted yet (they need to vote), OR
  //   (b) some users voted but not all have voted yet (waiting for other participant)
  // Don't block if the last vote concluded with "continue" — participants chose to keep communicating
  const lastVoteResult = conflict.steps?.communicate?.lastVoteResult;
  const votesWereCleared = lastVoteResult === 'continue' && Object.keys(satisfactionVotes).length === 0;
  
  // Is the satisfaction vote UI visible right now? If so, hide all waiting/turn blocks and Because form
  const showSatisfactionVoteUI = communicationIsComplete && isMentee && canEdit && cycles.length < maxEntries && !votesWereCleared;
  const pendingSatisfactionVote = showSatisfactionVoteUI;
  const canSubmitBecause = canSubmitBecauseBase && !pendingSatisfactionVote;

  const handleSatisfactionVote = async (satisfied) => {
    try {
      const updatedVotes = {
        ...satisfactionVotes,
        [user.id]: {
          satisfied,
          userId: user.id,
          userName: user.name,
      userAvatar: user.avatar,
          timestamp: new Date().toISOString()
        }
      };

      const allNowVoted = menteeIds.every(id => updatedVotes[id] !== undefined);
      const allNowSatisfied = allNowVoted && Object.values(updatedVotes).every(v => v.satisfied);
      const anyNowSaidContinue = Object.values(updatedVotes).some(v => !v.satisfied);

      let newStatus = conflict.status;
      let statusEntry = null;

      if (allNowSatisfied) {
        // Both satisfied — move to explore-alternatives
        newStatus = 'explore-alternatives';
        statusEntry = { status: 'explore-alternatives', timestamp: new Date().toISOString() };
      }

      const updated = {
        ...conflict,
        steps: {
          ...conflict.steps,
          communicate: {
            ...conflict.steps.communicate,
            satisfactionVotes: anyNowSaidContinue ? {} : updatedVotes, // Clear votes if someone said continue (so they can vote again later)
            // Track that a vote concluded with "continue" so the Because form unblocks
            ...(anyNowSaidContinue && { lastVoteResult: 'continue', lastVoteTimestamp: new Date().toISOString() }),
            ...(allNowSatisfied && { completed: true, lastVoteResult: 'satisfied' })
          },
          ...(statusEntry && {
            exploreAlternatives: {
              ...(conflict.steps.exploreAlternatives || {}),
              alternatives: conflict.steps.exploreAlternatives?.alternatives || []
            }
          })
        },
        ...(statusEntry && {
          status: newStatus,
          statusHistory: [
            ...conflict.statusHistory,
            statusEntry
          ]
        }),
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      addAuditEntry(updated, user.id, user.name, 'Voted on communication satisfaction', satisfied ? 'Satisfied, ready to move on' : 'Not satisfied, continue communicating');
      await storage.set(`conflict:${conflict.id}`, updated, true);
      await onUpdate();

      if (allNowSatisfied) {
        toast('Both participants are satisfied with the communication. Moving to Explore Alternatives!');
        if (onReturnToOverview) onReturnToOverview();
      } else if (anyNowSaidContinue) {
        toast('A participant would like to continue communicating. The vote has been reset. Continue sharing perspectives.');
        // If it's not this user's turn to submit a Because statement, send them to overview
        const lastCycleEntry = conflict.steps?.communicate?.cycles?.length > 0 
          ? conflict.steps.communicate.cycles[conflict.steps.communicate.cycles.length - 1] 
          : null;
        const isThisUsersTurn = !lastCycleEntry || (lastCycleEntry.paraphrase && lastCycleEntry.paraphrasedBy === user.id);
        if (!isThisUsersTurn && onReturnToOverview) onReturnToOverview();
      } else if (!allNowVoted) {
        toast('Your vote has been recorded. Waiting for the other participant to vote.');
        if (onReturnToOverview) onReturnToOverview();
      }
    } catch (error) {
      console.error('Error saving satisfaction vote:', error);
      toast('Failed to save your vote. Please try again.');
    }
  };

  // Calculate if wait time has passed for paraphrasing
  const canParaphraseNow = () => {
    if (!needsParaphrase) return false;
    const lastCycle = cycles[cycles.length - 1];
    const statementTime = new Date(lastCycle.timestamp);
    const now = new Date();
    const minutesPassed = (now - statementTime) / (1000 * 60);
    return minutesPassed >= conflict.responseWaitTime;
  };

  const getTimeUntilCanParaphrase = () => {
    if (!needsParaphrase) return 0;
    const lastCycle = cycles[cycles.length - 1];
    const statementTime = new Date(lastCycle.timestamp);
    const now = new Date();
    const minutesPassed = (now - statementTime) / (1000 * 60);
    const minutesRemaining = Math.max(0, conflict.responseWaitTime - minutesPassed);
    return minutesRemaining;
  };

  const getResponseWindowRemaining = () => {
    if (!needsParaphrase) return 0;
    const lastCycle = cycles[cycles.length - 1];
    if (!lastCycle) return 0;
    const statementTime = new Date(lastCycle.timestamp);
    const now = new Date();
    const minutesPassed = (now - statementTime) / (1000 * 60);
    const totalResponseWindow = conflict.responseWaitTime + conflict.responseWindow;
    return Math.max(0, totalResponseWindow - minutesPassed);
  };

  // Derived: is the paraphrase response late? Driven by responseWindowRemaining state.
  const paraphraseIsLate = needsParaphrase && responseWindowRemaining <= 0 && canParaphraseNow();

  const formatTimeRemaining = (minutes) => {
    const totalSeconds = Math.ceil(minutes * 60);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate time remaining for Because statement submission
  const getBecauseStatementTimeRemaining = () => {
    const lastCycle = cycles[cycles.length - 1];
    if (!lastCycle || !lastCycle.paraphrase) return 0;
    
    const paraphraseTime = new Date(lastCycle.paraphrasedAt);
    const now = new Date();
    const minutesPassed = (now - paraphraseTime) / (1000 * 60);
    const minutesRemaining = Math.max(0, conflict.responseWindow - minutesPassed);
    return minutesRemaining;
  };

  const isBecauseStatementLate = () => {
    if (cycles.length === 0) return false; // First statement, no time limit
    const lastCycle = cycles[cycles.length - 1];
    if (!lastCycle || !lastCycle.paraphrase) return false;
    return getBecauseStatementTimeRemaining() <= 0;
  };

  // Update timer every second — cooling period and response window
  React.useEffect(() => {
    if (!needsParaphrase) return;

    const updateTimers = () => {
      setTimeRemaining(getTimeUntilCanParaphrase());
      setResponseWindowRemaining(getResponseWindowRemaining());
    };

    updateTimers();
    const interval = setInterval(updateTimers, 1000);
    return () => clearInterval(interval);
  }, [needsParaphrase, cycles]);

  // Update Because statement timer every second
  React.useEffect(() => {
    if (canAddStatement && !needsParaphrase && cycles.length > 0) {
      setBecauseStatementTimeRemaining(getBecauseStatementTimeRemaining());
      
      const interval = setInterval(() => {
        const remaining = getBecauseStatementTimeRemaining();
        setBecauseStatementTimeRemaining(remaining);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [canAddStatement, needsParaphrase, cycles]);

  // Update status messages every minute for response window tracking
  React.useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render to update response window messages
      setTimeRemaining(getTimeUntilCanParaphrase());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [cycles]);

  // Update timer for the SUBMITTER showing cooling period/response window status
  React.useEffect(() => {
    const lastCycle = cycles[cycles.length - 1];
    if (lastCycle && !lastCycle.paraphrase && lastCycle.userId === user.id) {
      const updateSubmitterTimer = () => {
        const statementTime = new Date(lastCycle.timestamp);
        const now = new Date();
        const minutesPassed = (now - statementTime) / (1000 * 60);
        const totalResponseWindow = conflict.responseWaitTime + conflict.responseWindow;
        const remaining = Math.max(0, totalResponseWindow - minutesPassed);
        setSubmitterWaitTimeRemaining(remaining);
      };
      
      updateSubmitterTimer();
      const interval = setInterval(updateSubmitterTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [cycles, user.id, conflict.responseWaitTime, conflict.responseWindow]);

  // AUTO-TRANSITION: When all 5 communication cycles are complete, automatically move to explore-alternatives
  // This removes the need for a manual "Continue" button and prevents one user from unilaterally transitioning
  const autoTransitionRef = React.useRef(false);
  React.useEffect(() => {
    const allCyclesDone = cycles.length >= maxEntries && cycles[cycles.length - 1]?.paraphrase;
    if (allCyclesDone && conflict.status === 'communicate' && !autoTransitionRef.current) {
      autoTransitionRef.current = true;
      (async () => {
        try {
          const updated = {
            ...conflict,
            status: 'explore-alternatives',
            steps: {
              ...conflict.steps,
              communicate: { ...conflict.steps.communicate, completed: true },
              exploreAlternatives: {
                ...(conflict.steps.exploreAlternatives || {}),
                alternatives: conflict.steps.exploreAlternatives?.alternatives || []
              }
            },
            statusHistory: [
              ...conflict.statusHistory,
              { status: 'explore-alternatives', timestamp: new Date().toISOString() }
            ],
            updatedAt: new Date().toISOString(),
            updatedBy: user.id
          };
          addAuditEntry(updated, user.id, user.name, 'Communication completed', `All ${maxCycles} cycles finished, auto-transitioned to Explore Alternatives`);
          await storage.set(`conflict:${conflict.id}`, updated, true);
          await onUpdate();
        } catch (error) {
          console.error('Error auto-transitioning to explore-alternatives:', error);
          autoTransitionRef.current = false; // Allow retry on error
        }
      })();
    }
  }, [cycles, conflict.status]);


  const hasCycles = cycles.length > 0;

  const sectionPhaseInfo = (
    <div>
      <h4 className="font-medium text-stone-800 mb-3">Communication Phase</h4>
      <p className="text-sm text-stone-600 mb-4">
        Share your perspective using "Because" statements. Each statement must start with "Because", 
        include "I feel", and be 50-250 characters. After you submit, the other person paraphrases your statement 
        (after a cooling period), then immediately shares their own perspective, and vice versa. 
        A complete cycle is one exchange per person. Maximum {maxCycles} cycles ({maxEntries} total entries).
      </p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Phase info at top only when no cycles yet */}
      {!hasCycles && sectionPhaseInfo}

      {/* Communication Satisfaction Vote — shown to both mentees when a cycle is complete */}
      {showSatisfactionVoteUI && (
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-purple-600" />
            <h5 className="font-medium text-purple-800">Ready to Move On?</h5>
          </div>
          <p className="text-sm text-purple-700 mb-3">
            If you feel the communication has been thorough enough, you can propose moving to Explore Alternatives. 
            Both participants must agree. If either chooses to continue, you can keep communicating.
          </p>

          {/* Show current vote status */}
          {Object.keys(satisfactionVotes).length > 0 && !userSatisfactionVote && (
            <div className="mb-3 p-2 bg-purple-100 rounded border border-purple-200">
              <p className="text-xs text-purple-700">
                <strong>The other participant has voted.</strong> Please cast your vote below.
              </p>
            </div>
          )}

          {userSatisfactionVote && !allSatisfactionVoted && (
            <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
              <p className="text-xs text-blue-700">
                <strong>You voted: {userSatisfactionVote.satisfied ? 'Ready to move on' : 'Continue communicating'}</strong>. Waiting for the other participant to vote.
              </p>
            </div>
          )}

          {/* Vote buttons */}
          {!userSatisfactionVote && (
            <div className="flex gap-3">
              <button
                onClick={() => handleSatisfactionVote(true)}
                className="flex-1 bg-green-700 hover:bg-green-800 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                I'm Satisfied, Move On
              </button>
              <button
                onClick={() => handleSatisfactionVote(false)}
                className="flex-1 bg-amber-700 hover:bg-amber-800 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                Continue Communicating
              </button>
            </div>
          )}
        </div>
      )}

      {/* Add New Statement */}
      {canSubmitBecause && (
        <div ref={becauseFormRef} className="bg-white rounded-lg p-4 border-2 border-dashed border-amber-300">
          <h5 className="font-medium text-stone-800 mb-2">Share Your Perspective</h5>
          <p className="text-sm text-stone-600 mb-3">
            Complete the "Because" statement below. Include "I feel" to express your emotions, and aim for 50-250 characters total.
          </p>

          {/* Timer Display (show after first entry when last entry is paraphrased) */}
          {cycles.length > 0 && cycles[cycles.length - 1]?.paraphrase && (
            <div className="mb-4">
              {/* Immediate turn prompt for the paraphraser */}
              {didJustParaphrase && (
                <div className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Your Turn: Share Your Perspective Now</span>
                  </div>
                  <p className="text-xs text-green-700 mt-1">
                    You just paraphrased the other person's statement. It's now your turn to share your own "Because" statement.
                  </p>
                </div>
              )}
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-amber-800">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Response Window</span>
                  </div>
                  <div className="text-lg font-mono font-bold text-amber-900">
                    {formatTimeRemaining(becauseStatementTimeRemaining)}
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1">
                    <div className="h-2 bg-amber-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-amber-600 transition-all duration-1000 ease-linear"
                        style={{ 
                          width: `${Math.max(0, (becauseStatementTimeRemaining / conflict.responseWindow) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-amber-700">
                  {isBecauseStatementLate() ? (
                    <span className="text-red-700 font-medium">⚠️ Response window expired. You must provide a reason for late submission.</span>
                  ) : (
                    <>You have {conflict.responseWindow} minutes to submit your "Because" statement after the last paraphrase.</>
                  )}
                </p>
              </div>
            </div>
          )}

          <div className="relative">
            <textarea
              value={becauseStatement}
              onChange={(e) => handleBecauseChange(e.target.value)}
              className="w-full px-4 py-3 pr-12 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 mb-2"
              placeholder='Because I feel frustrated when plans change without notice, I need more communication about schedule updates.'
              rows={3}
              maxLength={250}
            />
            <VoiceInputButton 
              onResult={(text) => handleBecauseChange(becauseStatement + (becauseStatement ? ' ' : '') + text)}
              className="absolute right-2 top-2"
            />
          </div>

          {/* Say It Your Way — Because Statement Assistant */}
          {aiAssistEnabled && canEdit && !bsSiywOpen && !bsAiAssistUsed && (
            <div className="mb-3">
              <button
                onClick={openBsSiyw}
                className="w-full p-3 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg text-sm text-purple-700 font-medium transition-colors flex items-center justify-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                Need help? Say It Your Way: vent and we'll shape your statement
              </button>
            </div>
          )}

          <SayItYourWayCommunication
            isOpen={bsSiywOpen}
            onClose={() => setBsSiywOpen(false)}
            onExtracted={handleBsSiywExtracted}
            conflictContext={conflict.problemStatement?.what || conflict.title || ''}
            previousStatements={cycles.map(c => c.statement).join('; ')}
            trigger={bsAiAssistTrigger}
          />

          {/* Late Reason Field - Only show if response window expired */}
          {cycles.length > 0 && isBecauseStatementLate() && (
            <div className="mb-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <label htmlFor="late-submission-reason" className="block text-sm font-medium text-red-800 mb-2">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Required: Explain why you're submitting late
              </label>
              <textarea id="late-submission-reason"
                value={lateResponseReason}
                onChange={(e) => setLateResponseReason(e.target.value)}
                className="w-full px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                placeholder="Example: I needed more time to process my feelings..."
                rows={2}
                maxLength={200}
              />
              <div className="flex items-center justify-between mt-1">
                <span className={`text-xs ${
                  containsProfanity(lateResponseReason)
                    ? 'text-red-700'
                    : lateResponseReason.trim().length < 10 
                    ? 'text-red-700' 
                    : 'text-green-700'
                }`}>
                  {containsProfanity(lateResponseReason)
                    ? '⚠️ Contains inappropriate language'
                    : `${lateResponseReason.length}/200 characters ${lateResponseReason.trim().length < 10 ? '(min 10)' : ''}`}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm ${
              becauseStatement.length > 250 
                ? 'text-red-700 font-medium' 
                : becauseStatement.length >= 50 
                ? 'text-green-700 font-medium' 
                : 'text-stone-500'
            }`}>
              {becauseStatement.length}/250 characters {becauseStatement.length < 50 ? `(need ${50 - becauseStatement.length} more)` : ''}
            </span>
          </div>

          {/* Live Validation Checklist — always visible */}
          <div className={`mb-3 p-3 rounded-lg border-2 transition-all ${
            validationShake ? 'animate-pulse border-red-400 bg-red-50' : 'border-stone-200 bg-stone-50'
          }`}
            style={validationShake ? { animation: 'shake 0.4s ease-in-out 0s 2' } : {}}
          >
            <style>{`
              @keyframes shake {
                0%, 100% { transform: translateX(0); }
                25% { transform: translateX(-6px); }
                75% { transform: translateX(6px); }
              }
            `}</style>
            <div className="text-xs font-semibold text-stone-700 mb-2">Requirements:</div>
            <div className="space-y-1.5">
              <div className={`text-sm flex items-center gap-2 font-medium ${
                becauseStatement.length >= 50 && becauseStatement.length <= 250 
                  ? 'text-green-700' 
                  : validationShake ? 'text-red-600' : 'text-stone-500'
              }`}>
                {becauseStatement.length >= 50 && becauseStatement.length <= 250 ? (
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                )}
                50 to 250 characters
              </div>
              <div className={`text-sm flex items-center gap-2 font-medium ${
                becauseStatement.toLowerCase().startsWith('because') 
                  ? 'text-green-700' 
                  : validationShake ? 'text-red-600' : 'text-stone-500'
              }`}>
                {becauseStatement.toLowerCase().startsWith('because') ? (
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                )}
                Starts with "Because"
              </div>
              <div className={`text-sm flex items-center gap-2 font-medium ${
                becauseStatement.toLowerCase().includes('i feel') 
                  ? 'text-green-700' 
                  : validationShake ? 'text-red-600' : 'text-stone-500'
              }`}>
                {becauseStatement.toLowerCase().includes('i feel') ? (
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                )}
                Includes "I feel"
              </div>
              <div className={`text-sm flex items-center gap-2 font-medium ${
                !containsProfanity(becauseStatement) 
                  ? 'text-green-700' 
                  : 'text-red-600'
              }`}>
                {!containsProfanity(becauseStatement) ? (
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <X className="w-4 h-4 flex-shrink-0" />
                )}
                No profanity or offensive language
              </div>
              {bsAiAssistUsed && (
                <div className={`text-sm flex items-center gap-2 font-medium ${
                  meetsBsEditRequirement(becauseStatement) 
                    ? 'text-green-700' 
                    : 'text-amber-700'
                }`}>
                  {meetsBsEditRequirement(becauseStatement) ? (
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  )}
                  AI suggestion edited (at least 10 characters changed)
                </div>
              )}
            </div>
          </div>

          {/* Connotation / Word Choice Suggestions for Because statement */}
          {becauseConnotation && becauseConnotation.hasIssues && !becauseConnotation._dismissed && (
            <div className="mb-3 p-3 rounded-lg border bg-blue-50 border-blue-200">
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800 mb-2">Word choice suggestion</p>
                  {becauseConnotation.suggestions.map((s, i) => (
                    <div key={i} className="mb-2 last:mb-0">
                      <p className="text-xs text-blue-700 mb-1">{s.context}</p>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded line-through">"{s.found}"</span>
                        <span className="text-blue-600">→</span>
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">"{s.suggestion}"</span>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setBecauseConnotation({ ...becauseConnotation, _dismissed: true })} className="text-xs text-blue-600 hover:text-blue-800 font-medium underline">Keep my wording and submit</button>
                    <span className="text-xs text-blue-600">|</span>
                    <button onClick={() => setBecauseConnotation(null)} className="text-xs text-green-700 hover:text-green-800 font-medium underline">I'll revise my text</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleSubmitBecause}
            disabled={cycles.length > 0 && isBecauseStatementLate() && (lateResponseReason.trim().length < 10 || containsProfanity(lateResponseReason))}
            className={`w-full px-6 py-3 rounded-lg font-medium transition-colors ${
              validateBecauseStatement(becauseStatement) && !(bsAiAssistUsed && !meetsBsEditRequirement(becauseStatement))
                ? 'bg-amber-700 hover:bg-amber-800 text-white'
                : 'bg-amber-400 hover:bg-amber-500 text-white cursor-pointer'
            } disabled:bg-stone-300 disabled:cursor-not-allowed`}
          >
            Submit Statement
          </button>
        </div>
      )}
      {/* Waiting because it's the other mentee's turn to submit a Because statement */}
      {conflict.status === 'communicate' && isWaitingForOtherTurn && !isWaitingForOtherMentee && !pendingSatisfactionVote && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 text-blue-800 mb-2">
            <Clock className="w-5 h-5" />
            <span className="font-medium">Waiting for Other Participant</span>
          </div>
          <p className="text-sm text-blue-700">
            The other participant just paraphrased your statement and is now composing their "Because" statement. 
            Once they submit, you'll need to paraphrase it, and then it will be your turn to share again.
          </p>
        </div>
      )}

      {/* Blocked because another mentee is actively composing the first entry */}
      {isBlockedByActiveComposer && !isWaitingForOtherTurn && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 text-blue-800 mb-2">
            <MessageSquare className="w-5 h-5" />
            <span className="font-medium">Participant Is Composing</span>
          </div>
          <p className="text-sm text-blue-700">
            {conflict.mentees?.find(m => m.id === activeComposer)?.name || 'Another participant'} is currently writing their "Because" statement. 
            The entry field will become available if they don't submit within 5 minutes.
          </p>
        </div>
      )}

      {/* Status Message at Bottom */}
      {cycles.length >= maxEntries && cycles[cycles.length - 1]?.paraphrase && (
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between mb-1">
            <p className="text-green-800 font-medium">Communication Complete</p>
            <p className="text-xs text-green-700">
              {new Date(cycles[cycles.length - 1].paraphrasedAt).toLocaleString()}
            </p>
          </div>
          <p className="text-green-700 text-sm mb-3">All {maxCycles} communication cycles completed. {conflict.status === 'communicate' ? 'Transitioning to Explore Alternatives...' : 'Moved to Explore Alternatives.'}</p>
        </div>
      )}

      {cycles.length > 0 && (cycles.length < maxEntries || !cycles[cycles.length - 1]?.paraphrase) && (
        <div>
          {/* User is waiting for other person to paraphrase their statement */}
          {cycles[cycles.length - 1] && !cycles[cycles.length - 1].paraphrase && cycles[cycles.length - 1].userId === user.id && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-blue-800">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">Awaiting Paraphrase</span>
                </div>
                <span className="text-xs text-blue-600">
                  Submitted: {new Date(cycles[cycles.length - 1].timestamp).toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-blue-700 mb-2">
                Your "Because" statement has been shared. The other participant needs to paraphrase your statement 
                before the conversation can continue.
              </p>
              {(() => {
                const statementTime = new Date(cycles[cycles.length - 1].timestamp);
                const now = new Date();
                const minutesPassed = (now - statementTime) / (1000 * 60);
                const coolingPeriodRemaining = Math.max(0, conflict.responseWaitTime - minutesPassed);
                const totalResponseWindow = conflict.responseWaitTime + conflict.responseWindow;
                const responseWindowRemaining = Math.max(0, totalResponseWindow - minutesPassed);
                
                if (coolingPeriodRemaining > 0) {
                  // Still in cooling period
                  return (
                    <div className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded">
                      ⏱️ Cooling period: {Math.ceil(coolingPeriodRemaining)} minute{Math.ceil(coolingPeriodRemaining) !== 1 ? 's' : ''} remaining before they can respond
                    </div>
                  );
                } else if (responseWindowRemaining > 0) {
                  // Cooling period over, in response window
                  return (
                    <div className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded">
                      ⏰ Response window: {Math.ceil(responseWindowRemaining)} minute{Math.ceil(responseWindowRemaining) !== 1 ? 's' : ''} remaining for them to respond
                    </div>
                  );
                } else {
                  // Response window expired
                  return (
                    <div className="text-xs text-red-700 bg-red-100 px-2 py-1 rounded">
                      ⚠️ Response window has expired
                    </div>
                  );
                }
              })()}
            </div>
          )}

          {/* User needs to paraphrase the other person's statement */}
          {cycles[cycles.length - 1] && !cycles[cycles.length - 1].paraphrase && cycles[cycles.length - 1].userId !== user.id && (
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-orange-800">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Action Required: Paraphrase Response</span>
                </div>
                <span className="text-xs text-orange-700">
                  Received: {new Date(cycles[cycles.length - 1].timestamp).toLocaleString()}
                </span>
              </div>
              {(() => {
                if (!canParaphraseNow()) {
                  // Still in cooling period - cannot respond yet
                  return (
                    <>
                      <p className="text-sm text-orange-700 mb-2">
                        A cooling period is in effect to allow for thoughtful responses.
                        You'll be able to paraphrase the statement below once the timer expires.
                      </p>
                      <div className="text-xs text-orange-700 bg-orange-100 px-2 py-1 rounded">
                        ⏱️ Cooling period: {Math.ceil(timeRemaining)} minute{Math.ceil(timeRemaining) !== 1 ? 's' : ''} remaining before you can respond
                      </div>
                    </>
                  );
                } else if (!paraphraseIsLate) {
                  // Cooling period over, can respond now
                  return (
                    <>
                      <p className="text-sm text-orange-700 mb-2">
                        Please paraphrase the statement below to show your understanding.
                        After submitting your paraphrase, you'll be able to share your own perspective.
                      </p>
                      <div className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                        ✓ You can now respond • {Math.ceil(responseWindowRemaining)} minute{Math.ceil(responseWindowRemaining) !== 1 ? 's' : ''} remaining in response window
                      </div>
                    </>
                  );
                } else {
                  // Response window expired
                  return (
                    <>
                      <p className="text-sm text-orange-700 mb-2">
                        The response window has expired, but you can still paraphrase the statement below.
                      </p>
                      <div className="text-xs text-red-700 bg-red-100 px-2 py-1 rounded">
                        ⚠️ Response window has expired
                      </div>
                    </>
                  );
                }
              })()}
            </div>
          )}

          {/* Turn-aware status: only the paraphraser sees "Your Turn", the other sees "Waiting" */}
          {/* Hidden when satisfaction vote is pending — the vote UI takes priority */}
          {/* Hidden when the isWaitingForOtherTurn block above is already showing */}
          {cycles[cycles.length - 1]?.paraphrase && cycles.length < maxEntries && !pendingSatisfactionVote && !isWaitingForOtherTurn && (
            (() => {
              const lastParaphraser = cycles[cycles.length - 1].paraphrasedBy;
              const isMyTurn = lastParaphraser === user.id;
              
              if (isMyTurn) {
                return (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 text-green-800 mb-2">
                      <MessageSquare className="w-5 h-5" />
                      <span className="font-medium">Your Turn to Share</span>
                    </div>
                    <p className="text-sm text-green-700">
                      You just paraphrased the other person's statement. It's now your turn to share your perspective by adding a "Because" statement above.
                    </p>
                  </div>
                );
              } else {
                return (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-800 mb-2">
                      <Clock className="w-5 h-5" />
                      <span className="font-medium">Waiting for Other Participant's Turn</span>
                    </div>
                    <p className="text-sm text-blue-700">
                      The other participant paraphrased your statement and is now composing their "Because" statement. 
                      Once they submit, you'll need to paraphrase it, and then it will be your turn again.
                    </p>
                  </div>
                );
              }
            })()
          )}
        </div>
      )}

      {cycles.length === 0 && (
        <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
          {canSubmitBecause ? (
            <>
              <p className="text-stone-700 font-medium">Begin Communication</p>
              <p className="text-stone-600 text-sm mt-1">
                Start the conversation by sharing your perspective with a "Because" statement above.
              </p>
            </>
          ) : (
            <>
              <p className="text-stone-700 font-medium">Communication Phase</p>
              <p className="text-stone-600 text-sm mt-1">
                Waiting for the first participant to share their perspective.
              </p>
            </>
          )}
        </div>
      )}

      {/* Existing Cycles — reversed (newest first) when cycles exist */}
      <div className="space-y-4">
        {[...cycles].map((cycle, idx) => ({ cycle, idx })).reverse().map(({ cycle, idx }) => (
          <div key={idx} className="bg-white rounded-lg p-4 border border-stone-200 overflow-hidden">
            <div className="flex items-start gap-3 mb-3">
              <AnimalAvatar animal={cycle.userAvatar} size={32} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-stone-800 break-words">{cycle.userName}</span>
                  {cycle.statementAiAssisted && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      AI-assisted
                    </span>
                  )}
                </div>
                <p className="text-stone-700 break-words">{cycle.statement}</p>
                <p className="text-xs text-stone-500 mt-2">
                  {new Date(cycle.timestamp).toLocaleString()}
                </p>
                {cycle.lateSubmission && (
                  <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                    <div className="flex items-center gap-1 text-xs text-red-700 font-medium mb-1">
                      <AlertCircle className="w-3 h-3" />
                      Late Submission ({cycle.minutesLate} min late)
                    </div>
                    <p className="text-xs text-red-700 italic break-words">"{cycle.lateReason}"</p>
                  </div>
                )}
                {/* Mentor comments on Because statement */}
                <MentorInlineComment entryId={`comm-statement-${idx}`} entryType="Because statement" conflict={conflict} user={user} onUpdate={onUpdate} />
                <MentorCommentsDisplay entryId={`comm-statement-${idx}`} conflict={conflict} user={user} />
              </div>
            </div>

            {cycle.paraphrase && (
              <div className="ml-11 pl-4 border-l-2 border-green-200 mt-3">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm text-stone-600">Paraphrased:</p>
                  {cycle.aiAssisted && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      AI-assisted
                    </span>
                  )}
                </div>
                <p className="text-stone-700 break-words">{cycle.paraphrase}</p>
                {cycle.lateResponse && (
                  <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                    <div className="flex items-center gap-1 text-xs text-red-700 font-medium mb-1">
                      <AlertCircle className="w-3 h-3" />
                      Late Response ({cycle.minutesLate} min late)
                    </div>
                    <p className="text-xs text-red-700 italic break-words">"{cycle.lateResponseReason}"</p>
                  </div>
                )}
                {/* Mentor comments on paraphrase */}
                <MentorInlineComment entryId={`comm-paraphrase-${idx}`} entryType="paraphrase" conflict={conflict} user={user} onUpdate={onUpdate} />
                <MentorCommentsDisplay entryId={`comm-paraphrase-${idx}`} conflict={conflict} user={user} />
              </div>
            )}

            {/* Waiting indicator for the SUBMITTER - shows that cooling period is active for the other person */}
            {!cycle.paraphrase && idx === cycles.length - 1 && cycle.userId === user.id && (
              <div className="ml-11 mt-3">
                {(() => {
                  const statementTime = new Date(cycle.timestamp);
                  const now = new Date();
                  const minutesPassed = (now - statementTime) / (1000 * 60);
                  const coolingPeriodRemaining = Math.max(0, conflict.responseWaitTime - minutesPassed);
                  const isInCoolingPeriod = coolingPeriodRemaining > 0;
                  const totalResponseWindow = conflict.responseWaitTime + conflict.responseWindow;
                  const responseWindowRemaining = Math.max(0, totalResponseWindow - minutesPassed);
                  
                  // Use submitterWaitTimeRemaining state to force re-renders every second
                  // (the calculation above uses fresh Date values on each re-render)
                  void submitterWaitTimeRemaining; // Reference state to ensure component re-renders
                  
                  if (isInCoolingPeriod) {
                    return (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 text-blue-800 mb-2">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-medium">Cooling Period Active</span>
                        </div>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="text-2xl font-mono font-bold text-blue-900">
                            {formatTimeRemaining(coolingPeriodRemaining)}
                          </div>
                          <div className="flex-1">
                            <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-600 transition-all duration-1000 ease-linear"
                                style={{ 
                                  width: `${Math.max(0, 100 - (coolingPeriodRemaining / conflict.responseWaitTime * 100))}%` 
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-blue-700">
                          The other participant must wait before they can respond. This cooling period helps ensure thoughtful, calm responses.
                        </p>
                      </div>
                    );
                  } else if (responseWindowRemaining > 0) {
                    return (
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 text-amber-800 mb-2">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-medium">Awaiting Response</span>
                        </div>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="text-2xl font-mono font-bold text-amber-900">
                            {formatTimeRemaining(responseWindowRemaining)}
                          </div>
                          <div className="flex-1">
                            <div className="h-2 bg-amber-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-amber-600 transition-all duration-1000 ease-linear"
                                style={{ 
                                  width: `${Math.max(0, 100 - (responseWindowRemaining / totalResponseWindow * 100))}%` 
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-amber-700">
                          The other participant can now respond. Waiting for their paraphrase of your statement.
                        </p>
                      </div>
                    );
                  } else {
                    return (
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-2 text-red-800 mb-2">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Response Overdue</span>
                        </div>
                        <p className="text-xs text-red-700">
                          The response window has passed. The other participant may still respond with a late submission reason.
                        </p>
                      </div>
                    );
                  }
                })()}
              </div>
            )}

            {!cycle.paraphrase && idx === cycles.length - 1 && needsParaphrase && (
              <div className="ml-11 mt-3 bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h6 className="text-sm font-medium text-blue-800 mb-2">Paraphrase to Show Understanding</h6>
                <p className="text-xs text-blue-700 mb-3">Restate their perspective in your own words (50-250 characters, no profanity)</p>
                
                {/* Wait Time Notice */}
                {!canParaphraseNow() && (
                  <div className="mb-3 p-3 bg-amber-50 rounded-lg border border-amber-300">
                    <div className="flex items-center gap-2 text-amber-800 mb-2">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">Cooling Period Active</span>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="text-2xl font-mono font-bold text-amber-900">
                        {formatTimeRemaining(timeRemaining)}
                      </div>
                      <div className="flex-1">
                        <div className="h-2 bg-amber-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-amber-600 transition-all duration-1000 ease-linear"
                            style={{ 
                              width: `${Math.max(0, 100 - (timeRemaining / conflict.responseWaitTime * 100))}%` 
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-amber-700">
                      This cooling period helps ensure thoughtful, calm responses. 
                      You'll be able to paraphrase when the timer reaches 0:00.
                    </p>
                  </div>
                )}

                <div className="relative">
                  <textarea
                    value={paraphrase}
                    onChange={(e) => handleParaphraseChange(e.target.value)}
                    disabled={!canParaphraseNow()}
                    className="w-full px-4 py-3 pr-12 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 mb-2 disabled:bg-stone-100 disabled:cursor-not-allowed"
                    placeholder={canParaphraseNow() ? "Paraphrase their statement to show understanding..." : "Wait for cooling period to end..."}
                    rows={3}
                    maxLength={250}
                  />
                  {canParaphraseNow() && (
                    <VoiceInputButton 
                      onResult={(text) => handleParaphraseChange(paraphrase + (paraphrase ? ' ' : '') + text)}
                      className="absolute right-2 top-2"
                    />
                  )}
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs ${
                    paraphrase.length > 250 
                      ? 'text-red-700 font-medium' 
                      : paraphrase.length >= 50 
                      ? 'text-green-700 font-medium' 
                      : 'text-stone-500'
                  }`}>
                    {paraphrase.length}/250 characters {paraphrase.length < 50 ? `(need ${50 - paraphrase.length} more)` : ''}
                  </span>
                </div>

                {/* AI Paraphrase Assistance Panel */}
                {/* Paraphrase Quality Warning */}
                {paraphraseWarning && canParaphraseNow() && (
                  <div className={`mb-3 p-3 rounded-lg border ${
                    paraphraseWarning.type === 'argument' 
                      ? 'bg-red-50 border-red-200' 
                      : paraphraseWarning.type === 'self_statement'
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-start gap-2">
                      <AlertCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        paraphraseWarning.type === 'argument' ? 'text-red-600' : 'text-orange-600'
                      }`} />
                      <div>
                        <p className={`text-sm font-medium mb-1 ${
                          paraphraseWarning.type === 'argument' ? 'text-red-800' : 'text-orange-800'
                        }`}>
                          {paraphraseWarning.type === 'self_statement' && '📝 This looks like your own statement'}
                          {paraphraseWarning.type === 'argument' && '⚠️ This looks like an argument'}
                          {paraphraseWarning.type === 'off_topic' && '🔍 This doesn\'t seem to match their statement'}
                        </p>
                        <p className={`text-xs mb-2 ${
                          paraphraseWarning.type === 'argument' ? 'text-red-700' : 'text-orange-700'
                        }`}>
                          {paraphraseWarning.message}
                        </p>
                        <div className={`text-xs p-2 rounded ${
                          paraphraseWarning.type === 'argument' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                        }`}>
                          <strong>💡 Tip:</strong> {paraphraseWarning.tip}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {aiAssistEnabled && aiAssistAvailable && canParaphraseNow() && (
                  <div className="mb-3">
                    {!aiPanelOpen && aiSuggestions.length === 0 && (
                      <button
                        onClick={generateAiSuggestions}
                        disabled={aiSuggestionsLoading}
                        className="w-full p-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-sm text-blue-700 font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        {aiSuggestionsLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating suggestions...
                          </>
                        ) : (
                          <>
                            <MessageSquare className="w-4 h-4" />
                            Need help? Get AI paraphrase suggestions
                          </>
                        )}
                      </button>
                    )}

                    {aiPanelOpen && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 text-blue-800">
                            <MessageSquare className="w-4 h-4" />
                            <span className="text-sm font-medium">AI Suggestions</span>
                          </div>
                          <button
                            onClick={() => setAiPanelOpen(false)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-blue-600 mb-3">
                          Select a suggestion as a starting point, then <strong>edit it to make it your own</strong> (change at least 10 characters). 
                          AI-assisted paraphrases are flagged in the record.
                        </p>

                        {aiSuggestionsLoading && (
                          <div className="flex items-center justify-center py-4 text-blue-600">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Generating suggestions...
                          </div>
                        )}

                        {aiSuggestionsError && (
                          <div className="p-2 bg-red-50 rounded border border-red-200 text-xs text-red-700 mb-2">
                            {aiSuggestionsError}
                            <button onClick={generateAiSuggestions} className="ml-2 underline">Try again</button>
                          </div>
                        )}

                        {aiSuggestions.length > 0 && (
                          <div className="space-y-2">
                            {aiSuggestions.map((suggestion, sIdx) => (
                              <button
                                key={sIdx}
                                onClick={() => {
                                  handleParaphraseChange(suggestion);
                                  setSelectedAiSuggestion(sIdx);
                                  setAiAssistUsed(true);
                                }}
                                className={`w-full text-left p-3 rounded-lg border text-sm transition-colors ${
                                  selectedAiSuggestion === sIdx
                                    ? 'bg-blue-100 border-blue-400 text-blue-900'
                                    : 'bg-white border-stone-200 text-stone-700 hover:bg-blue-50 hover:border-blue-300'
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <span className="text-xs font-medium text-blue-600 mt-0.5 flex-shrink-0">
                                    {sIdx + 1}.
                                  </span>
                                  <span className="break-words">{suggestion}</span>
                                </div>
                              </button>
                            ))}
                            <button
                              onClick={generateAiSuggestions}
                              disabled={aiSuggestionsLoading}
                              className="w-full text-center text-xs text-blue-600 hover:text-blue-800 py-1"
                            >
                              {aiSuggestionsLoading ? 'Generating...' : 'Regenerate suggestions'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Late Response Reason - Only show if response window expired */}
                {paraphraseIsLate && (
                  <div className="mb-3 p-3 bg-red-50 rounded-lg border border-red-200">
                    <label htmlFor="late-response-reason" className="block text-sm font-medium text-red-800 mb-2">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Required: Explain why you missed the response window
                    </label>
                    <textarea id="late-response-reason"
                      value={lateResponseReason}
                      onChange={(e) => setLateResponseReason(e.target.value)}
                      className="w-full px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                      placeholder="Example: I was in a meeting and couldn't respond in time..."
                      rows={2}
                      maxLength={200}
                    />
                    <div className="flex items-center justify-between mt-1">
                      <span className={`text-xs ${
                        containsProfanity(lateResponseReason)
                          ? 'text-red-700'
                          : lateResponseReason.trim().length < 10
                          ? 'text-red-700'
                          : 'text-green-700'
                      }`}>
                        {containsProfanity(lateResponseReason)
                          ? '⚠️ Contains inappropriate language'
                          : `${lateResponseReason.length}/200 characters ${lateResponseReason.trim().length < 10 ? '(min 10)' : ''}`}
                      </span>
                    </div>
                  </div>
                )}

                {/* Validation Checklist for Paraphrase */}
                {paraphrase.length > 0 && canParaphraseNow() && (
                  <div className="mb-3 p-2 bg-white rounded border border-stone-200">
                    <div className="text-xs font-medium text-stone-700 mb-1">Requirements:</div>
                    <div className="space-y-1">
                      <div className={`text-xs flex items-center gap-2 ${
                        paraphrase.length >= 50 && paraphrase.length <= 250 
                          ? 'text-green-700' 
                          : 'text-stone-500'
                      }`}>
                        {paraphrase.length >= 50 && paraphrase.length <= 250 ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <AlertCircle className="w-4 h-4" />
                        )}
                        50-250 characters
                      </div>
                      <div className={`text-xs flex items-center gap-2 ${
                        !containsProfanity(paraphrase)
                          ? 'text-green-700'
                          : 'text-red-700'
                      }`}>
                        {!containsProfanity(paraphrase) ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                        No profanity or offensive language
                      </div>
                      {aiAssistUsed && (
                        <div className={`text-xs flex items-center gap-2 ${
                          meetsEditRequirement(paraphrase)
                            ? 'text-green-700'
                            : 'text-amber-700'
                        }`}>
                          {meetsEditRequirement(paraphrase) ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <AlertCircle className="w-4 h-4" />
                          )}
                          AI suggestion edited (at least 10 characters changed)
                        </div>
                      )}
                      <div className={`text-xs flex items-center gap-2 ${
                        !paraphraseWarning
                          ? 'text-green-700'
                          : paraphraseWarning.type === 'argument'
                          ? 'text-red-700'
                          : 'text-amber-700'
                      }`}>
                        {!paraphraseWarning ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : paraphraseWarning.type === 'argument' ? (
                          <X className="w-4 h-4" />
                        ) : (
                          <AlertCircle className="w-4 h-4" />
                        )}
                        {!paraphraseWarning 
                          ? 'Reflects the other person\'s statement' 
                          : paraphraseWarning.type === 'self_statement'
                          ? 'Appears to be your own statement, not a paraphrase'
                          : paraphraseWarning.type === 'argument'
                          ? 'Appears to be an argument, not a paraphrase'
                          : 'May not relate to the original statement'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Connotation / Word Choice Suggestions for Paraphrase */}
                {paraphraseConnotation && paraphraseConnotation.hasIssues && !paraphraseConnotation._dismissed && (
                  <div className="mb-3 p-3 rounded-lg border bg-blue-50 border-blue-200">
                    <div className="flex items-start gap-2">
                      <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800 mb-2">Word choice suggestion</p>
                        {paraphraseConnotation.suggestions.map((s, i) => (
                          <div key={i} className="mb-2 last:mb-0">
                            <p className="text-xs text-blue-700 mb-1">{s.context}</p>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded line-through">"{s.found}"</span>
                              <span className="text-blue-600">→</span>
                              <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">"{s.suggestion}"</span>
                            </div>
                          </div>
                        ))}
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => setParaphraseConnotation({ ...paraphraseConnotation, _dismissed: true })} className="text-xs text-blue-600 hover:text-blue-800 font-medium underline">Keep my wording and submit</button>
                          <span className="text-xs text-blue-600">|</span>
                          <button onClick={() => setParaphraseConnotation(null)} className="text-xs text-green-700 hover:text-green-800 font-medium underline">I'll revise my text</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleSubmitParaphrase(idx)}
                  disabled={
                    !canParaphraseNow() ||
                    !validateParaphrase(paraphrase) ||
                    (aiAssistUsed && !meetsEditRequirement(paraphrase)) ||
                    (paraphraseIsLate && lateResponseReason.trim().length < 10) ||
                    (paraphraseIsLate && containsProfanity(lateResponseReason))
                  }
                  className="w-full bg-amber-700 hover:bg-amber-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {!canParaphraseNow() ? `Wait ${formatTimeRemaining(timeRemaining)}` : 'Submit Paraphrase'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Phase info at bottom when cycles exist */}
      {hasCycles && sectionPhaseInfo}
    </div>
  );
}

// Follow-up Step

export default CommunicateStep;
